import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useTour } from "../../context/TourContext";
import ChickenLogo from "../ChickenLogo";
import "./Tour.css";

const TARGET_POLL_INTERVAL = 150;
const TARGET_POLL_TIMEOUT = 2500;

const findTarget = (target) => {
  const selectors = Array.isArray(target) ? target : [target];
  for (const id of selectors) {
    const el = document.querySelector(`[data-tour="${id}"]`);
    if (el) return el;
  }
  return null;
};

const isMobileViewport = () =>
  typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

const TourEngine = () => {
  const { isActive, stepIndex, steps, next, previous, stopTour } = useTour();
  const navigate = useNavigate();
  const location = useLocation();

  const [rect, setRect] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [ready, setReady] = useState(false);
  const runIdRef = useRef(0);

  const step = steps[stepIndex];

  const measure = useCallback((target) => {
    const el = findTarget(target);
    if (!el) return null;
    return el.getBoundingClientRect();
  }, []);

  useEffect(() => {
    if (!isActive || !step) return undefined;

    const runId = ++runIdRef.current;
    let cancelled = false;
    setReady(false);
    setNotFound(false);
    setRect(null);

    const resolveAndLocate = async () => {
      const targetPath = step.path ?? (step.resolvePath ? await step.resolvePath() : "/");
      if (cancelled || runIdRef.current !== runId) return;

      if (location.pathname !== targetPath) {
        navigate(targetPath);
        // Laisse le temps au routeur/à la page de rendre avant de chercher la cible.
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
      if (cancelled || runIdRef.current !== runId) return;

      const startedAt = Date.now();
      const poll = () => {
        if (cancelled || runIdRef.current !== runId) return;
        const el = findTarget(step.target);
        if (el) {
          el.scrollIntoView({ behavior: "auto", block: "center" });
          // Laisse le scroll s'appliquer avant de mesurer la position finale.
          setTimeout(() => {
            if (cancelled || runIdRef.current !== runId) return;
            setRect(el.getBoundingClientRect());
            setReady(true);
          }, 60);
          return;
        }
        if (Date.now() - startedAt > TARGET_POLL_TIMEOUT) {
          setNotFound(true);
          setReady(true);
          return;
        }
        setTimeout(poll, TARGET_POLL_INTERVAL);
      };
      poll();
    };

    resolveAndLocate();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, stepIndex, step]);

  useEffect(() => {
    if (!isActive || !ready || notFound) return undefined;
    const handleReposition = () => setRect(measure(step.target));
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isActive, ready, notFound, measure, step]);

  if (!isActive || !step || !ready) return null;

  const mobile = isMobileViewport();
  const hasSpotlight = rect && !notFound && !mobile;

  const bubbleStyle = hasSpotlight ? computeBubblePosition(rect) : {};

  return (
    <div className="tour-root">
      {/* Bloque tous les clics sur la vraie app pendant le tour (certains boutons
          déclenchent de vraies actions — paiement, annulation de commande...). */}
      <div className="tour-click-blocker" onClick={(e) => e.stopPropagation()} />

      {hasSpotlight ? (
        <div
          className="tour-spotlight-ring"
          style={{
            top: rect.top - 8,
            left: rect.left - 8,
            width: rect.width + 16,
            height: rect.height + 16,
          }}
        />
      ) : (
        <div className="tour-dim-overlay" />
      )}

      <div className={`tour-bubble-wrapper ${mobile ? "tour-bubble-mobile" : ""}`} style={mobile ? {} : bubbleStyle}>
        <div className="tour-bubble">
          <div className="tour-bubble-avatar">
            <ChickenLogo size={24} fill="white" />
            <span className="tour-bubble-avatar-pulse" />
          </div>
          <div className="tour-bubble-card">
            <button className="tour-bubble-close" onClick={stopTour} aria-label="Fermer la visite guidée">
              <X size={16} strokeWidth={2.25} />
            </button>
            <div className="tour-bubble-eyebrow">Assistant PouletDirect</div>
            <h3 className="tour-bubble-title">{step.title}</h3>
            <p className="tour-bubble-text">{step.text}</p>
            <div className="tour-bubble-footer">
              <span className="tour-bubble-progress">{stepIndex + 1} / {steps.length}</span>
              <div className="tour-bubble-actions">
                {stepIndex > 0 && (
                  <button className="btn btn-outline btn-sm" onClick={previous}>Précédent</button>
                )}
                <button className="btn btn-primary btn-sm" onClick={next}>
                  {stepIndex + 1 >= steps.length ? "Terminer" : "Suivant"}
                </button>
              </div>
            </div>
            <button className="tour-bubble-skip" onClick={stopTour}>Passer le tour</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BUBBLE_WIDTH = 340;
const BUBBLE_MARGIN = 16;
// Le contenu tient sur peu de lignes, mais on prévoit large : la cible peut
// être plus grande que le viewport (ex. un conteneur pleine page), auquel cas
// "espace au-dessus/en-dessous" ne veut plus dire grand-chose — on ancre donc
// toujours via `top`, jamais `bottom`, et on clamp strictement dans l'écran.
const ESTIMATED_BUBBLE_HEIGHT = 260;

function computeBubblePosition(rect) {
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  const spaceBelow = viewportH - rect.bottom;
  const spaceAbove = rect.top;
  const placeBelow = spaceBelow >= spaceAbove;

  let top = placeBelow ? rect.bottom + 16 : rect.top - 16 - ESTIMATED_BUBBLE_HEIGHT;
  top = Math.min(Math.max(top, BUBBLE_MARGIN), viewportH - ESTIMATED_BUBBLE_HEIGHT - BUBBLE_MARGIN);

  let left = rect.left + rect.width / 2 - BUBBLE_WIDTH / 2;
  left = Math.min(Math.max(left, BUBBLE_MARGIN), viewportW - BUBBLE_WIDTH - BUBBLE_MARGIN);

  return { top, left };
}

export default TourEngine;
